import { first, map, Observable, of, ReplaySubject, tap } from 'rxjs';
import { AssetLoader, LoadingRequestObject } from 'src/app/configurator/product/interfaces/AssetLoader';
import { LoadingService } from 'src/app/core/services/loading.service';
import { ProductBlueprint } from 'src/app/models/product-models/views/ProductBlueprint';
import { TextureData } from 'src/app/models/product-models/views/TextureData';
import { RepeatWrapping, SRGBColorSpace, Texture, Vector2, Vector3 } from 'three';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';

export enum TextureTypeEnum {
  Texture = 'Texture',
  Map = 'Map',
}
export abstract class ThreeJsLoader {
  private _assetLoader: AssetLoader;

  private _textureLoaded: ReplaySubject<Texture> = new ReplaySubject<Texture>(1);
  public textureLoaded$: Observable<Texture> = this._textureLoaded.asObservable();

  constructor(private loadingService: LoadingService) {
    this._assetLoader = new AssetLoader(this.loadingService);
  }

  //TODO, poskrbi da se ne uporablja pooling kadar ima isti material različne nastavitve materiala kot je metalness
  loadTextureData(
    textureData: TextureData,
    absoluteTextureVector?: Vector3,
  ): (Observable<Texture> | Observable<null>)[] {
    const usePooling = absoluteTextureVector ? false : true;

    const repeat = textureData.uvMapScale
      ? new Vector2(textureData.uvMapScale || 1, textureData.uvMapScale || 1)
      : new Vector2(1, 1);

    const textureObservable$ = textureData.textureUrl
      ? this.loadTexture$(
          [{ url: textureData.textureUrl + '?test=true', priority: 1 }],
          repeat,
          TextureTypeEnum.Texture,
          usePooling,
        ).pipe(first())
      : of(null);

    const normalMapObservable$ = textureData.normalMapUrl
      ? this.loadTexture$(
          [{ url: textureData.normalMapUrl + '?test=true', priority: 1 }],
          repeat,
          TextureTypeEnum.Map,
          usePooling,
        ).pipe(first())
      : of(null);

    const bumpMapObservable$ = textureData.bumpMapUrl
      ? this.loadTexture$(
          [{ url: textureData.bumpMapUrl + '?test=true', priority: 1 }],
          repeat,
          TextureTypeEnum.Map,
          usePooling,
        ).pipe(first())
      : of(null);

    const roughnessMapObservable$ = textureData.sheenRoughnessMapUrl
      ? this.loadTexture$(
          [{ url: textureData.sheenRoughnessMapUrl + '?test=true', priority: 1 }],
          repeat,
          TextureTypeEnum.Map,
          usePooling,
        ).pipe(first())
      : of(null);

    return [textureObservable$, normalMapObservable$, bumpMapObservable$, roughnessMapObservable$];
  }

  loadTexture$(
    loadingRequestObjects: LoadingRequestObject[],
    repeat?: Vector2,
    type: TextureTypeEnum = TextureTypeEnum.Texture,
    usePooling = true,
  ): Observable<Texture> {
    return this._assetLoader.loadTexture(loadingRequestObjects, usePooling).pipe(
      map((texture) => {
        texture.wrapS = RepeatWrapping;
        texture.wrapT = RepeatWrapping;
        if (repeat) texture.repeat.copy(repeat);
        if (type === TextureTypeEnum.Texture) texture.colorSpace = SRGBColorSpace;
        return texture;
      }),
      tap((texture) => this._textureLoaded.next(texture)),
    );
  }

  loadGLTF$(loadingRequestObjects: LoadingRequestObject[]): Observable<GLTF> {
    return this._assetLoader.loadGLTF(loadingRequestObjects);
  }

  initializeLoadingForProductBlueprint(productBlueprint: ProductBlueprint, priority = 0) {
    if (productBlueprint.modelUrl) {
      productBlueprint.loadNewModel(
        this._assetLoader.loadGLTF([{ url: productBlueprint.modelUrl, priority: priority }]),
      );
    }
  }

  public resetPool() {
    this._assetLoader.resetPool();
  }

  /* initializeLoadingForProductBlueprints(productBlueprints:ProductBlueprint[],priority=0){
    const loadingRequestObjects:LoadingRequestObject[]=[]
    for( const productBlueprint of productBlueprints){
      loadingRequestObjects.push({url:productBlueprint.modelUrl,priority:priority})
    }
    this._assetLoader.loadGLTF([{url:productBlueprint.modelUrl,priority:priority}])
  } */
}
